import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getAttempt } from "@/lib/api";
import { shortId } from "@/lib/ids";
import KnowledgeStep from "@/components/steps/KnowledgeStep";
import CapabilityStep from "@/components/steps/CapabilityStep";
import ReflectionStep from "@/components/steps/ReflectionStep";
import Interstitial from "@/components/steps/Interstitial";
import ResultStep from "@/components/steps/ResultStep";
import DomainBadge from "@/components/DomainBadge";
import { useT } from "@/lib/i18n";

export default function AttemptRunner() {
  const { id } = useParams();
  const { t, lang } = useT();
  const [attempt, setAttempt] = useState(null);
  const [error, setError] = useState(null);

  // Iter-27 — language-aware refresh. `lang` is passed as a query
  // param so the backend returns localised presentation fields
  // (title / MCQ text / capability task / reflection prompt) from
  // the multilingual `concept_configs.language_pack`. Assessment
  // state (sequence, current_index, current_step, scoring,
  // behavioural events) is NEVER mutated by a language switch —
  // only the displayed text changes.
  const refresh = async () => {
    try {
      const a = await getAttempt(id, lang);
      setAttempt(a);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  // Initial load AND every time the candidate switches language.
  // The step components stay mounted across language changes, so
  // their internal state (selected MCQ answers, reflection draft,
  // capability sim progress) is preserved — only the visible text
  // re-renders.
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id, lang]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="attempt-error">
        <h1 className="font-display text-2xl font-bold">{t("runner.could_not_load")}</h1>
        <p className="text-sm text-[var(--iddl-muted)] mt-2">{error}</p>
      </div>
    );
  }
  if (!attempt) return <div className="max-w-3xl mx-auto px-6 py-16 label-mono">{t("common.loading")}</div>;

  const concept = attempt.current_concept;
  const step = attempt.current_step;
  const stepLabelMap = {
    knowledge: t("step.knowledge_layer").split("·")[1]?.trim() || step,
    capability: t("step.capability_layer").split("·")[1]?.trim() || step,
    reflection: t("step.reflection_layer").split("·")[1]?.trim() || step,
  };

  return (
    <div data-testid="attempt-runner" className="iddl-watermark">
      {/* progress strip */}
      <div className="border-b border-[var(--iddl-rule)] surface">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center gap-3 text-xs">
          <span className="label-mono">{t("runner.attempt")}</span>
          <code className="font-mono-disp">{shortId(attempt.id)}</code>
          <span className="label-mono ms-4">{t("runner.sequence")}</span>
          <ProgressDots attempt={attempt} />
          <div className="ms-auto flex items-center gap-3">
            <span className="label-mono">{t("runner.candidate")}</span>
            <span className="font-mono-disp text-[12px]">{attempt.candidate_name}</span>
          </div>
        </div>
      </div>

      {/* concept header */}
      {concept && step !== "complete" && step !== "interstitial" && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 pb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <DomainBadge domain={concept.domain} label={concept.domain_name} />
            <span className="label-mono">{concept.id}</span>
            <span className="label-mono">·</span>
            <span className="label-mono">{concept.subdomain}</span>
            {concept.core && <span className="label-mono text-[var(--iddl-ink)]">· CORE</span>}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3">
            {concept.title}
          </h1>
          <p className="text-sm text-[var(--iddl-muted)] mt-2">
            {t("runner.concept_of", { n: attempt.current_index + 1, total: attempt.sequence_ids.length })}{" "}
            <span>{stepLabelMap[step] || step}</span>
          </p>
        </div>
      )}

      {step === "knowledge" && <KnowledgeStep attempt={attempt} onAdvance={refresh} />}
      {step === "capability" && <CapabilityStep attempt={attempt} onAdvance={refresh} />}
      {step === "reflection" && <ReflectionStep attempt={attempt} onAdvance={refresh} />}
      {step === "interstitial" && <Interstitial attempt={attempt} onAdvance={refresh} />}
      {step === "complete" && <ResultStep attempt={attempt} />}
    </div>
  );
}

function ProgressDots({ attempt }) {
  const order = ["DS", "CS", "AI"];
  const stepOrder = ["knowledge", "capability", "reflection"];
  return (
    <div className="flex items-center gap-2">
      {attempt.sequence_ids.map((cid, i) => {
        const dom = order[i];
        const isPast = i < attempt.current_index || attempt.current_step === "complete";
        const isCurrent = i === attempt.current_index && attempt.current_step !== "complete";
        return (
          <div key={cid} className="flex items-center gap-2">
            <span className={`label-mono ${dom === "DS" ? "text-ds" : dom === "CS" ? "text-cs" : "text-ai"}`}>{dom}</span>
            <div className="flex gap-0.5">
              {stepOrder.map((s) => {
                const filled =
                  isPast ||
                  (isCurrent && stepOrder.indexOf(attempt.current_step) > stepOrder.indexOf(s)) ||
                  (isCurrent && attempt.current_step === s);
                const cur = isCurrent && attempt.current_step === s;
                return (
                  <span
                    key={s}
                    className={`inline-block w-3 h-1.5 ${filled ? (dom === "DS" ? "bg-ds" : dom === "CS" ? "bg-cs" : "bg-ai") : "bg-[var(--iddl-rule)]"} ${cur ? "ring-1 ring-offset-1 ring-[var(--iddl-ink)]" : ""}`}
                  />
                );
              })}
            </div>
            {i < 2 && <span className="text-[var(--iddl-muted)]">→</span>}
          </div>
        );
      })}
    </div>
  );
}
